import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import * as React from "react"

import { ExportButton } from "@/features/exports/export-button"
import { DataTable, useListParams, type Column } from "@/components/data-table"
import { EmployeeAvatar } from "@/components/employee-avatar"
import { Button, PageHeader, Select } from "@/components/ui"
import {
  useEmployeeFacets,
  useEmployeeList,
  type EmployeeOrdering,
  type EmployeeRow,
} from "@/features/employees/api"
import { EmployeeForm } from "@/features/employees/employee-form"
import { fullName } from "@/features/employees/fields"
import { ActiveBadge, NotRecorded } from "@/features/employees/text"
import { PAGE_SIZE } from "@/lib/constants"
import { useSession } from "@/lib/session"

export const Route = createFileRoute("/employees/")({ component: EmployeesScreen })

function EmployeesScreen() {
  const navigate = useNavigate()
  const { canWrite } = useSession()
  const { page, setPage, ordering, setOrdering, search, setSearch, debouncedSearch } =
    useListParams<EmployeeOrdering>("last_name")
  const [includeInactive, setIncludeInactive] = React.useState(false)
  const [site, setSite] = React.useState("")
  const [department, setDepartment] = React.useState("")
  const [employmentType, setEmploymentType] = React.useState("")
  const [creating, setCreating] = React.useState(false)

  const facets = useEmployeeFacets()
  const list = useEmployeeList({
    page,
    ordering,
    q: debouncedSearch,
    includeInactive,
    site,
    department,
    employmentType,
  })

  // Every filter narrows the result set, and a narrower set rarely has the
  // page you were on.
  function filterWith(set: (value: string) => void) {
    return (value: string) => {
      set(value)
      setPage(1)
    }
  }

  // The export carries the same filters as the screen, because the file
  // holding a different set of people from the table is the silent failure
  // the shared search function exists to prevent.
  const exportParams = {
    q: debouncedSearch,
    ordering,
    ...(includeInactive ? { include_inactive: true } : {}),
    ...(site ? { site } : {}),
    ...(department ? { department } : {}),
    ...(employmentType ? { employment_type: employmentType } : {}),
  }

  function openEmployee(id: number | null | undefined) {
    if (id == null) return
    void navigate({ to: "/employees/$employeeId", params: { employeeId: String(id) } })
  }

  // The list row is lean by design: these six fields are all the endpoint
  // returns, and each sortable key is one the server will sort on.
  const columns: Column<EmployeeRow>[] = [
    {
      key: "last_name",
      header: "Name",
      primary: true,
      sortable: true,
      cell: (row) => (
        <span className="flex items-center gap-3">
          <EmployeeAvatar
            firstName={row.first_name}
            lastName={row.last_name}
            department={row.functional_area}
          />
          <span className="min-w-0">
            <span className="block truncate font-medium">{fullName(row)}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {row.employee_number}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: "employee_number",
      header: "Employee number",
      sortable: true,
      hideOnMobile: true,
      cell: (row) => row.employee_number,
    },
    {
      key: "job_title",
      header: "Job title",
      sortable: true,
      cell: (row) => row.job_title || <NotRecorded />,
    },
    {
      // "Department" is the client's word; `functional_area` is the column the
      // design chose for it until they confirm which one they mean.
      key: "functional_area",
      header: "Department",
      sortable: true,
      cell: (row) => row.functional_area || <NotRecorded />,
    },
    {
      key: "active",
      header: "Status",
      cell: (row) => <ActiveBadge active={row.active} />,
    },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Workforce"
        title="Employees"
        description="Master employee data, normally loaded from the SAP export. Someone who leaves is marked inactive and kept, never deleted."
        actions={
          <div className="flex items-start gap-2">
            <ExportButton slug="employees" params={exportParams} />
            {canWrite && (
              <Button onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                New employee
              </Button>
            )}
          </div>
        }
      />

      <DataTable
        columns={columns}
        rows={list.data?.items ?? []}
        count={list.data?.count ?? 0}
        rowKey={(row) => row.id ?? row.employee_number}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        ordering={ordering}
        onOrderingChange={setOrdering}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name or employee number"
        isLoading={list.isLoading}
        isRefreshing={list.isFetching && !list.isPending}
        isError={list.isError}
        onRetry={() => void list.refetch()}
        onRowClick={(row) => openEmployee(row.id)}
        emptyTitle={
          debouncedSearch || site || department || employmentType
            ? "Nobody matches those filters"
            : "No employees yet"
        }
        emptyDescription={
          debouncedSearch || site || department || employmentType
            ? "Search runs on the employee number, first name and last name; the dropdowns narrow by site, department and employment type."
            : includeInactive
              ? "Employees arrive with the SAP import, or can be captured one at a time."
              : "Terminated employees are hidden. Include them to see people who have left."
        }
        filters={
          <>
            <FilterSelect
              label="Site"
              value={site}
              onChange={filterWith(setSite)}
              options={facets.data?.sites ?? []}
            />
            <FilterSelect
              label="Department"
              value={department}
              onChange={filterWith(setDepartment)}
              options={facets.data?.departments ?? []}
            />
            <FilterSelect
              label="Employment type"
              value={employmentType}
              onChange={filterWith(setEmploymentType)}
              options={facets.data?.employment_types ?? []}
            />
            <label className="flex items-center gap-2 whitespace-nowrap pl-1 text-sm">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(event) => {
                  // A wider set rarely has the page you were on.
                  setIncludeInactive(event.target.checked)
                  setPage(1)
                }}
                className="h-4 w-4 rounded border"
              />
              Include terminated
            </label>
          </>
        }
        summary={
          facets.data && (
            <>
              <span className="chip">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
                {facets.data.total} on the books
              </span>
              <span className="chip">
                <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
                {facets.data.active} active
              </span>
              <span className="chip">
                <span className="h-1.5 w-1.5 rounded-full bg-notice" aria-hidden />
                {facets.data.contractors} contractors
              </span>
            </>
          )
        }
      />

      {creating && (
        <EmployeeForm
          employee={null}
          onClose={() => setCreating(false)}
          onSaved={(saved) => {
            setCreating(false)
            openEmployee(saved.id)
          }}
        />
      )}
    </>
  )
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: string[]
}) {
  return (
    <Select
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-auto min-w-40 [&>button]:h-9 [&>button]:rounded-full [&>button]:text-xs"
    >
      <option value="">All {label.toLowerCase()}s</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </Select>
  )
}
